/* eslint-disable react/prop-types */
import React, {useRef} from 'react';
import {useFocusEffect} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {noop} from 'lodash';

import ErrorBoundaryProvider from '/provider/ErrorBoundaryProvider';
import {withStatusBar} from '/provider/StatusBarProvider';
import {withHOC} from '/util/hoc.util';

import ScreenRenderer from './ScreenRenderer';
import {cardStyle} from './StackProvider.style';

interface StackProviderProps {
  children: React.ReactNode;
  statusBarSetDarkText: () => void;
  statusBarSetLightText: () => void;
}

export class StackProvider extends React.PureComponent<StackProviderProps> {
  getModalStackComponent() {
    const ModalStackNavigator = createStackNavigator();
    const Content = this.getCardStackComponent();
    const Renderer = this.getScreenRendererComponent({modalType: 'modal'});

    return () => (
      <ModalStackNavigator.Navigator
        initialRouteName="content-with-modal"
        screenOptions={{
          headerShown: false,
          presentation: 'modal',
          cardStyle,
        }}>
        <ModalStackNavigator.Screen
          name="content-with-modal"
          component={Content}
        />
        <ModalStackNavigator.Screen name="modal" component={Renderer} />
      </ModalStackNavigator.Navigator>
    );
  }

  getCardStackComponent() {
    const CardStackNavigator = createStackNavigator();
    const Content = this.getChildrenComponent();
    const Renderer = this.getScreenRendererComponent({modalType: 'card'});

    return () => (
      <CardStackNavigator.Navigator
        initialRouteName="content-with-card"
        screenOptions={{
          headerShown: false,
          cardStyle,
        }}>
        <CardStackNavigator.Screen
          name="content-with-card"
          component={Content}
        />
        <CardStackNavigator.Screen name="card" component={Renderer} />
      </CardStackNavigator.Navigator>
    );
  }

  getScreenRendererComponent({modalType}: {modalType: string}) {
    const {statusBarSetDarkText = noop, statusBarSetLightText = noop} =
      this.props;

    return ({
      navigation: {canGoBack, goBack, navigate},
      route: {params},
    }: any) => {
      const {modalName, modalProps, statusBarStyle} = params;

      useFocusEffect(() => {
        if (statusBarStyle === 'light') {
          statusBarSetLightText();
        } else {
          statusBarSetDarkText();
        }
      });

      const handleCloseRef = useRef(() => {
        if (modalProps?.previousScreenName) {
          navigate('card', {
            modalName: modalProps.previousScreenName,
            modalProps: modalProps.previousScreenProps || {},
          });
        } else if (canGoBack()) {
          goBack();
        } else {
          if (modalType === 'card') {
            navigate('content-with-card');
          } else {
            navigate('content-with-modal');
          }
        }
      });

      return (
        <ErrorBoundaryProvider hasBack>
          <ScreenRenderer
            modalType={modalType}
            modalName={modalName}
            modalProps={modalProps}
            onClose={handleCloseRef.current}
          />
        </ErrorBoundaryProvider>
      );
    };
  }

  getChildrenComponent() {
    const {children} = this.props;
    return () => <>{children}</>;
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  render() {
    const ModalStack = this.getModalStackComponent();
    return <ModalStack />;
  }
}

export default withHOC(withStatusBar)(StackProvider);
